import { Tooltip } from "@design-system/DataDisplay/Tooltip";
import { Switch } from "@design-system/Inputs/Switch";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { Text } from "@design-system/Typography/Text";
import { HandCard } from "iconoir-react";
import { useAtom } from "jotai";
import { useTranslation } from "react-i18next";
import { InitializeTenantDocument } from "../../../../generated/client/graphql";
import { useGraphQLMutation } from "../../../QueryClientWithHeaders";
import ButtonRegular from "../../../components/Button/ButtonRegular";
import { ButtonType } from "../../../components/Button/ButtonShared";
import {
    CenterModal,
    CenterModalContent,
} from "../../../components/Modal/CenterModal";
import { useSetToast } from "../../../components/Toast";
import { orgToConfigureAtom } from "../ctx";
import {
    useActivateModule,
    useDeactivateModule,
    useModuleConfiguration,
} from "./data";
import "./i18n";

type Module = {
    name: string;
    moduleId: string;
    moduleType: string;
    productType: string;
    isActive: boolean;
};

const ConfigureModal = () => {
    const { t } = useTranslation("ConfigureOrgModal");

    const [org, setOrg] = useAtom(orgToConfigureAtom);

    const { moduleConfiguration } = useModuleConfiguration({
        tenantId: org?.tenantId || "",
    });

    const { mutate: activateModule } = useActivateModule();
    const { mutate: deactivateModule } = useDeactivateModule();

    const { setToastSuccess } = useSetToast();

    const initializeTenantMutation = useGraphQLMutation(
        InitializeTenantDocument,
    );
    const onInitialiseTenant = () => {
        org &&
            initializeTenantMutation.mutate(
                { tenantId: org.tenantId },
                {
                    onSuccess: () => {
                        setToastSuccess(t("tenant_initialization_success"));
                        setOrg(undefined);
                    },
                },
            );
    };

    const onActivateModule = (module: Module) => {
        if (!org?.tenantId) return;
        activateModule({
            moduleId: module.moduleId,
            moduleType: module.moduleType,
            productType: module.productType,
            tenantId: org.tenantId,
        });
    };

    const onDeactivateModule = (module: Module) => {
        if (!org?.tenantId) return;
        deactivateModule({
            moduleId: module.moduleId,
            moduleType: module.moduleType,
            productType: module.productType,
            tenantId: org.tenantId,
        });
    };

    const handleToggleModule = (module: Module) => {
        if (module.isActive) {
            onDeactivateModule(module);
        } else {
            onActivateModule(module);
        }
    };

    return (
        <>
            <CenterModal
                open={org !== undefined}
                onOpenChange={(open) => !open && setOrg(undefined)}
                modal={false}
            >
                <CenterModalContent
                    title={t("title")}
                    contentClassName="[&>div>div:nth-child(3)]:pt-0 [&>div>div:nth-child(3)]:pb-3"
                >
                    {moduleConfiguration?.map((module) => (
                        <FlexCol
                            p="4"
                            gap="4"
                            key={module.moduleId}
                            className="border rounded-lg"
                        >
                            <FlexRow justifyContent="between">
                                <Text variant="body-bold">{module.name}</Text>
                                <Tooltip>
                                    <Tooltip.Trigger>
                                        <Switch
                                            checked={module.isActive}
                                            onChange={() =>
                                                handleToggleModule(module)
                                            }
                                            disabled={
                                                module.moduleId ===
                                                "management-system-module"
                                            }
                                        />
                                    </Tooltip.Trigger>
                                    {module.moduleId ===
                                        "management-system-module" && (
                                        <Tooltip.Content>
                                            <Text variant="body-sm">
                                                Management system module cannot
                                                be disabled
                                            </Text>
                                        </Tooltip.Content>
                                    )}
                                </Tooltip>
                            </FlexRow>

                            {module.addOns.length > 0 && (
                                <FlexCol gap="2">
                                    {module.addOns.map((addon) => (
                                        <FlexRow
                                            justifyContent="between"
                                            key={addon.moduleId}
                                        >
                                            <Text variant="body-sm">
                                                • {addon.name}
                                            </Text>
                                            <Switch
                                                checked={addon.isActive}
                                                onChange={() =>
                                                    handleToggleModule(addon)
                                                }
                                            />
                                        </FlexRow>
                                    ))}
                                </FlexCol>
                            )}
                        </FlexCol>
                    ))}

                    <ButtonRegular
                        text={t("button_init_all")}
                        variant={ButtonType.DEFAULT}
                        IconRight={HandCard}
                        onClick={onInitialiseTenant}
                        loading={initializeTenantMutation.isPending}
                    />
                </CenterModalContent>
            </CenterModal>
        </>
    );
};
export default ConfigureModal;
